import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Settings, Upload, X, Image, Palette, MapPin } from 'lucide-react';

const PRESET_COLORS = [
  { name: 'Blue', color: '#3b82f6' },
  { name: 'Navy', color: '#1e3a5f' },
  { name: 'Orange', color: '#f97316' },
  { name: 'Red', color: '#dc2626' },
  { name: 'Green', color: '#16a34a' },
  { name: 'Purple', color: '#7c3aed' },
  { name: 'Teal', color: '#0d9488' },
  { name: 'Gold', color: '#ca8a04' },
  { name: 'Maroon', color: '#7f1d1d' },
  { name: 'Black', color: '#1e293b' },
];

const DEFAULT_BRANDING = {
  logoUrl: '',
  organizationName: '',
  primaryColor: '#3b82f6',
  accentColor: '#f97316',
  textMode: 'light',
  mapCenter: null,
  mapZoom: 14,
  showReservationDemo: true,
};

// Helper to lighten a hex color for backgrounds
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BrandingSettings = ({ branding, onBrandingChange }) => {
  const [open, setOpen] = useState(false);
  const [localBranding, setLocalBranding] = useState(branding || DEFAULT_BRANDING);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLocalBranding(branding || DEFAULT_BRANDING);
  }, [branding]);

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setLocalBranding(prev => ({ ...prev, logoUrl: e.target.result }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRemoveLogo = () => {
    setLocalBranding(prev => ({ ...prev, logoUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    onBrandingChange(localBranding);
    localStorage.setItem('parkingBranding', JSON.stringify(localBranding));
    setOpen(false);
  };

  const handleReset = () => {
    setLocalBranding(DEFAULT_BRANDING);
    onBrandingChange(DEFAULT_BRANDING);
    localStorage.removeItem('parkingBranding');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center space-x-2"
          style={{
            color: branding?.textMode === 'dark' ? '#1e293b' : '#ffffff',
            borderColor: branding?.textMode === 'dark' ? 'rgba(30,41,59,0.3)' : 'rgba(255,255,255,0.4)',
            backgroundColor: 'transparent',
          }}
        >
          <Settings className="w-4 h-4" />
          <span>Branding</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-800">Branding Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Logo Upload */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <Image className="w-4 h-4" />
              <span>Logo</span>
            </label>
            {localBranding.logoUrl ? (
              <div className="space-y-3">
                <div className="relative inline-block">
                  <img
                    src={localBranding.logoUrl}
                    alt="Organization logo"
                    className="max-h-20 max-w-full object-contain rounded-lg border border-slate-200 p-2 bg-white"
                  />
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload-replace"
                  />
                  <label
                    htmlFor="logo-upload-replace"
                    className="cursor-pointer inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Upload className="w-3 h-3" />
                    <span>Replace logo</span>
                  </label>
                </div>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-center">
                    <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                    <span className="text-sm text-slate-500">Click to upload logo</span>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Organization Name */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <span>Organization Name</span>
            </label>
            <Input
              type="text"
              placeholder="e.g., University of Florida, City of West Palm Beach"
              value={localBranding.organizationName}
              onChange={(e) => setLocalBranding(prev => ({ ...prev, organizationName: e.target.value }))}
              className="w-full"
            />
            <p className="text-xs text-slate-500 mt-1">This will appear as the main title in the header bar</p>
          </div>

          {/* Primary Color */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <Palette className="w-4 h-4" />
              <span>Primary Color</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Used for header background, buttons, and main accents</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.color}
                  onClick={() => setLocalBranding(prev => ({ ...prev, primaryColor: preset.color }))}
                  className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                    localBranding.primaryColor === preset.color ? 'border-slate-800 ring-2 ring-offset-1 ring-slate-400' : 'border-slate-200'
                  }`}
                  style={{ backgroundColor: preset.color }}
                  title={preset.name}
                />
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={localBranding.primaryColor}
                onChange={(e) => setLocalBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-10 h-10 rounded-lg cursor-pointer border border-slate-300"
              />
              <Input
                type="text"
                value={localBranding.primaryColor}
                onChange={(e) => setLocalBranding(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-32"
                placeholder="#3b82f6"
              />
            </div>
          </div>

          {/* Accent Color */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <Palette className="w-4 h-4" />
              <span>Accent Color</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Used for highlights, badges, and secondary elements</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={`accent-${preset.color}`}
                  onClick={() => setLocalBranding(prev => ({ ...prev, accentColor: preset.color }))}
                  className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                    localBranding.accentColor === preset.color ? 'border-slate-800 ring-2 ring-offset-1 ring-slate-400' : 'border-slate-200'
                  }`}
                  style={{ backgroundColor: preset.color }}
                  title={preset.name}
                />
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={localBranding.accentColor || '#f97316'}
                onChange={(e) => setLocalBranding(prev => ({ ...prev, accentColor: e.target.value }))}
                className="w-10 h-10 rounded-lg cursor-pointer border border-slate-300"
              />
              <Input
                type="text"
                value={localBranding.accentColor || '#f97316'}
                onChange={(e) => setLocalBranding(prev => ({ ...prev, accentColor: e.target.value }))}
                className="w-32"
                placeholder="#f97316"
              />
            </div>
          </div>

          {/* Live Preview Bar */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Preview</label>
            <div 
              className="rounded-lg p-4 flex items-center space-x-3"
              style={{ backgroundColor: localBranding.primaryColor }}
            >
              {localBranding.logoUrl && (
                <img src={localBranding.logoUrl} alt="Preview logo" className="h-8 w-auto object-contain" />
              )}
              <span 
                className="font-bold text-lg"
                style={{ color: localBranding.textMode === 'dark' ? '#1e293b' : '#ffffff' }}
              >
                {localBranding.organizationName || 'Organization Name'}
              </span>
              <div className="ml-auto flex items-center space-x-2">
                <span 
                  className="px-2 py-1 rounded text-white text-xs font-medium"
                  style={{ backgroundColor: localBranding.accentColor || '#f97316' }}
                >
                  Accent
                </span>
              </div>
            </div>
          </div>

          {/* Text Mode */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <span>Header Text</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Choose light text for dark backgrounds, dark text for light backgrounds</p>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setLocalBranding(prev => ({ ...prev, textMode: 'light' }))}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  localBranding.textMode === 'light' 
                    ? 'border-blue-500 bg-slate-800 ring-2 ring-blue-200' 
                    : 'border-slate-200 bg-slate-700 hover:border-slate-300'
                }`}
              >
                <span className="text-white font-semibold text-sm">Light Text</span>
              </button>
              <button
                onClick={() => setLocalBranding(prev => ({ ...prev, textMode: 'dark' }))}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  localBranding.textMode === 'dark' 
                    ? 'border-blue-500 bg-white ring-2 ring-blue-200' 
                    : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                }`}
              >
                <span className="text-slate-800 font-semibold text-sm">Dark Text</span>
              </button>
            </div>
          </div>

          {/* Map Starting Position (optional override) */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <MapPin className="w-4 h-4" />
              <span>Map Position Override</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Leave empty to auto-center on your parking locations</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Latitude</label>
                <Input
                  type="number"
                  step="0.0001"
                  value={localBranding.mapCenter?.lat ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setLocalBranding(prev => ({ ...prev, mapCenter: null }));
                    } else {
                      setLocalBranding(prev => ({
                        ...prev,
                        mapCenter: { lat: parseFloat(val) || 0, lng: prev.mapCenter?.lng || 0 }
                      }));
                    }
                  }}
                  placeholder="Auto"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Longitude</label>
                <Input
                  type="number"
                  step="0.0001"
                  value={localBranding.mapCenter?.lng ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setLocalBranding(prev => ({ ...prev, mapCenter: null }));
                    } else {
                      setLocalBranding(prev => ({
                        ...prev,
                        mapCenter: { lat: prev.mapCenter?.lat || 0, lng: parseFloat(val) || 0 }
                      }));
                    }
                  }}
                  placeholder="Auto"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Zoom Level</label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={localBranding.mapZoom}
                  onChange={(e) => setLocalBranding(prev => ({
                    ...prev,
                    mapZoom: parseInt(e.target.value) || 14
                  }))}
                  placeholder="14"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={handleReset}
              className="text-slate-600"
            >
              Reset to Defaults
            </Button>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="text-white"
                style={{ backgroundColor: localBranding.primaryColor }}
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { DEFAULT_BRANDING, hexToRgba };
export default BrandingSettings;
